import { useRef, useCallback } from 'react';
import { overlayRectFor } from '../../../utils/anchoredOverlayRect';
import { resolveExpandCaret } from './syntaxHighlightHelpers';

/** Undo history, re-rendering the highlight, the editable ref, overflow expansion and input. */
export function useHighlightEditing({
  onChange,
  showDynamicVarPicker,
  inputRef,
  expandOnOverflow,
  isExpanded,
  setIsExpanded,
  isExpandedRef,
  setOverlayRect,
  editableRef,
  anchorRef,
  pendingCaretRef,
  lastCaretRef,
  suppressExpandRef,
  isUpdatingRef,
  lastValueRef,
  lastRenderedTextRef,
  createHighlightedHTML,
  getPlainText,
  getSelectionOffsets,
  setSelectionOffsets,
  openDynamicVarPicker,
}) {
  /**
   * Undo history.
   *
   * The field is a contentEditable that repaints itself by assigning
   * innerHTML, and a programmatic DOM write is not an editing command — it
   * wipes the browser's native undo stack. So Ctrl+Z did nothing here, in a
   * field where typing the wrong thing and wanting it back is completely
   * ordinary.
   *
   * Keystrokes are COALESCED rather than recorded one per character: undoing a
   * URL one letter at a time is not undo, it is a slow-motion replay. A run of
   * typing becomes one step, and a pause, a deletion or a paste starts a new
   * one — which is what every text field does and therefore what nobody has to
   * learn.
   */
  const undoStackRef = useRef([]);
  const redoStackRef = useRef([]);
  const lastCommitAtRef = useRef(0);
  // 'insert' | 'delete' — which kind of edit the pending step is made of.
  const lastOpRef = useRef(null);
  const COALESCE_MS = 500;
  const MAX_HISTORY = 200;

  const commitHistory = useCallback((text, selectionOffsets, { coalesce = true } = {}) => {
    const stack = undoStackRef.current;
    const top = stack[stack.length - 1];
    if (top && top.text === text) return;

    const now = Date.now();
    // Within the window, replace the pending step instead of adding one, so a
    // burst of typing collapses into a single undo.
    if (coalesce && top && now - lastCommitAtRef.current < COALESCE_MS) {
      lastCommitAtRef.current = now;
      return;
    }

    stack.push({ text, sel: selectionOffsets });
    if (stack.length > MAX_HISTORY) stack.shift();
    lastCommitAtRef.current = now;
  }, []);

  const renderHighlightedContent = useCallback((text, selectionOffsets = null, force = false) => {
    if (!editableRef.current) return;

    const normalizedText = text || '';
    if (!force && normalizedText === lastRenderedTextRef.current && !selectionOffsets) {
      return;
    }

    isUpdatingRef.current = true;
    // Plain text (no `{{`): use textContent — avoids innerHTML parse/normalization fighting spaces
    // and keeps caret stable when typing fast + Space next to deferred parent updates.
    const needsVariableHighlightDom = /\{\{/.test(normalizedText);
    if (needsVariableHighlightDom) {
      editableRef.current.innerHTML = createHighlightedHTML(normalizedText);
    } else {
      editableRef.current.textContent = normalizedText;
    }
    lastRenderedTextRef.current = normalizedText;
    // Restore the caret SYNCHRONOUSLY, in the same tick as the DOM rewrite.
    // Assigning textContent/innerHTML collapses the selection to offset 0 on a
    // focused contenteditable; deferring the restore to requestAnimationFrame
    // left a ~16ms window where the element was focused with the caret at the
    // start — a keystroke landing there was inserted at position 0 (stray char
    // at the front) and shifted the rest, scrambling the text. setSelectionOffsets
    // only walks text nodes (no layout needed), so it's safe to run immediately.
    // Callers pass selectionOffsets only when the field is (or is about to be)
    // focused, so this never steals focus.
    if (selectionOffsets) {
      setSelectionOffsets(selectionOffsets);
    }
    // Clear synchronously, before the browser can dispatch the next native `input` event.
    // Leaving this true let a fast second keystroke land while the flag was still
    // set, so handleInput's guard silently dropped it — losing that character.
    isUpdatingRef.current = false;
  }, [createHighlightedHTML, setSelectionOffsets, editableRef, isUpdatingRef, lastRenderedTextRef]);

  // `renderHighlightedContent`'s identity isn't stable across renders in this component (its
  // dependency chain traces back to variable-list props), so `setEditableRef` must NOT list it
  // as a useCallback dep — doing so previously made setEditableRef's own identity churn every
  // render, and React re-invokes ref callbacks (null, then node) whenever the callback identity
  // changes, even for the same DOM node. That forced a caret-resetting repaint on every keystroke.
  // Reading the latest function through a ref avoids that while still calling the current version.
  const renderHighlightedContentRef = useRef(renderHighlightedContent);
  renderHighlightedContentRef.current = renderHighlightedContent;

  const setEditableRef = useCallback((node) => {
    editableRef.current = node;
    if (inputRef) {
      if (typeof inputRef === 'function') {
        inputRef(node);
      } else {
        inputRef.current = node;
      }
    }
    // expand<->collapse swaps to a brand-new DOM node; the value-sync effect below won't
    // re-run for it (its deps are keyed on `value`, which hasn't changed), so repaint here.
    // Focus first, THEN repaint: renderHighlightedContent now restores the caret
    // synchronously, so the node must already be focused when it runs (focusing
    // afterward could otherwise reset the selection we just placed).
    if (expandOnOverflow && node) {
      // Only the EXPAND swap restores a caret and takes focus. Doing this on
      // the collapse swap re-focused the field the moment the user clicked
      // away, which re-triggered the overflow check and expanded it straight
      // back — the field could never collapse.
      const expanding = isExpandedRef.current;
      const offsets = expanding
        ? // Resolved against the text about to be painted, not the text at
          // capture time — fast typing puts characters in between.
          resolveExpandCaret(pendingCaretRef.current, (lastValueRef.current || '').length)
        : null;
      pendingCaretRef.current = null;
      // focus() fires handleFocus SYNCHRONOUSLY, and handleFocus re-baselines
      // lastValueRef from the node's DOM — but this node is brand-new and still
      // empty (nothing painted yet), so reading lastValueRef after focus() would
      // see '' and the repaint below would wipe the field (blur then commits the
      // empty string, deleting the URL). Snapshot the value first and restore the
      // baseline before painting.
      const text = lastValueRef.current;
      if (expanding) node.focus();
      lastValueRef.current = text;
      renderHighlightedContentRef.current(text, offsets, true);
    }
  }, [inputRef, expandOnOverflow, editableRef, isExpandedRef, lastValueRef, pendingCaretRef]);

  // Handle input changes with improved cursor handling
  // URL field only: if the text overflows the collapsed single line, snapshot the caret + anchor
  // position and switch to the floating multi-line overlay (see render below).
  const expandIfOverflowing = useCallback(() => {
    // Checked first and unconditionally: if isExpanded's early-return below fired instead while
    // this was pending, the flag would never get consumed and would wrongly suppress a later,
    // unrelated expand attempt.
    if (suppressExpandRef.current) {
      suppressExpandRef.current = false;
      return;
    }
    if (!expandOnOverflow || isExpanded) return;
    const el = editableRef.current;
    const anchor = anchorRef.current;
    if (!el || !anchor) return;
    if (el.scrollWidth > el.clientWidth + 1) {
      const capturedText = lastValueRef.current || '';
      const live = getSelectionOffsets() || lastCaretRef.current;
      pendingCaretRef.current = live
        ? { ...live, capturedLength: capturedText.length }
        : null;
      const rect = anchor.getBoundingClientRect();
      // Clipped to whatever scrolls around it: in a params/headers table the cell is
      // narrower than the overlay would be, and a portal inherits none of that
      // container's clipping (utils/anchoredOverlayRect).
      //
      // Falling back to the raw rect rather than refusing: the field was just focused,
      // so it IS on screen, and an environment that cannot measure (jsdom reports every
      // rect as zero) must still get a working expand. "Out of view, so collapse" is a
      // judgement only the scroll handler is entitled to make.
      const clamped = overlayRectFor(anchor) || { top: rect.top, left: rect.left, width: rect.width };
      // Capture the real collapsed height (rather than assuming a fixed 36/44px) so the
      // placeholder that reserves this row's space matches exactly — any mismatch shifts
      // everything below the URL bar down by the difference.
      setOverlayRect({ ...clamped, height: rect.height });
      setIsExpanded(true);
    }
  }, [expandOnOverflow, isExpanded, getSelectionOffsets, anchorRef, editableRef, lastCaretRef, lastValueRef, pendingCaretRef, setIsExpanded, setOverlayRect, suppressExpandRef]);

  const handleInput = useCallback(() => {
    if (isUpdatingRef.current || !editableRef.current) return;

    let plainText = getPlainText(editableRef.current);
    // URL field only: some WebViews (WKWebView/Safari) can insert real `\n`/`\r` characters
    // for what's meant to be pure soft/visual line-wrap in a multi-line contenteditable —
    // a URL has no meaningful newlines, so strip them at the source rather than fight the
    // WebView's internal editing behavior.
    if (expandOnOverflow && /[\r\n]/.test(plainText)) {
      plainText = plainText.replace(/[\r\n]+/g, '');
    }

    // Only proceed if text actually changed
    if (plainText !== lastValueRef.current) {
      // Snapshot what the field held BEFORE this change — that is what Ctrl+Z
      // has to return to.
      //
      // Steps break when the KIND of edit changes, not on every edit. Held
      // Backspace is one deletion to the person doing it, so consecutive
      // deletes coalesce exactly as consecutive keystrokes do; what starts a
      // fresh step is switching between inserting and deleting, because
      // collapsing those together would make one undo throw away both.
      const isDeletion = plainText.length < (lastValueRef.current ?? '').length;
      const op = isDeletion ? 'delete' : 'insert';
      commitHistory(lastValueRef.current ?? '', lastCaretRef.current, {
        coalesce: op === lastOpRef.current,
      });
      lastOpRef.current = op;
      redoStackRef.current = [];

      const selectionOffsets = getSelectionOffsets();
      // Kept so an expand that happens a frame later still has a caret to
      // restore even if the live selection is no longer resolvable.
      if (selectionOffsets) lastCaretRef.current = selectionOffsets;
      lastValueRef.current = plainText;

      // Update parent state immediately for responsiveness
      onChange?.({ target: { value: plainText } });
      if (/\{\{/.test(plainText)) {
        renderHighlightedContent(plainText, selectionOffsets);
      } else {
        // Plain text: browser already updated the DOM; only rewrite when stripping variable spans.
        // Emptied by select-all + Delete, the browser leaves a filler <br> (or an
      // empty text node) behind. getPlainText walks text nodes only, so it
      // reports '', React state already matches, and the value-sync effect
      // below early-returns — nothing ever clears it, and it renders as a
      // stray blank line until a second Backspace removes it as a real edit.
      if (plainText === '' && editableRef.current.childNodes.length > 0) {
        editableRef.current.textContent = '';
      }

      const hasHighlightDom = editableRef.current.querySelector?.('.variable-span');
        if (hasHighlightDom) {
          renderHighlightedContent(plainText, selectionOffsets);
        } else {
          lastRenderedTextRef.current = plainText;
        }
      }

      // Dynamic variable picker — re-evaluate after each edit (caret may lag behind onInput in WebView).
      if (showDynamicVarPicker) {
        requestAnimationFrame(() => openDynamicVarPicker());
      }

      // URL field only: text may have just grown past the fold — check after the DOM reflows.
      if (expandOnOverflow && !isExpanded) {
        requestAnimationFrame(() => expandIfOverflowing());
      }
    }
  }, [commitHistory, onChange, getSelectionOffsets, renderHighlightedContent, getPlainText, showDynamicVarPicker, openDynamicVarPicker, expandOnOverflow, isExpanded, expandIfOverflowing, editableRef, isUpdatingRef, lastCaretRef, lastRenderedTextRef, lastValueRef]);

  return {
    undoStackRef,
    redoStackRef,
    lastOpRef,
    commitHistory,
    renderHighlightedContent,
    setEditableRef,
    expandIfOverflowing,
    handleInput,
  };
}
